import { useState } from "react";
import clsx from "clsx";
import { zodResolver } from "@hookform/resolvers/zod";
import { EditIcon, EllipsisIcon, TrashIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { JobPipelineCandidateSheetVerificationBadge } from "@/components";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";

import {
  useCreateCandidacyComment,
  useDeleteCandidacyComment,
  useUpdateCandidacyComment,
} from "@/hooks/mutations";
import { useGetCandidacyComments, useGetUser } from "@/hooks/queries";
import { useRefetchCandidacyComments } from "@/hooks/refetches";
import { dates } from "@/lib/dates";
import { misc } from "@/lib/misc";
import { storage } from "@/lib/storage";
import { CandidacyComment as CandidacyCommentType, Role } from "@/types";

const addCandidacyCommentSchema = z.object({
  content: z.string().min(1, {
    message: "Required",
  }),
});
const AddCandidacyCommentForm = ({ candidacyId }: { candidacyId: string }) => {
  const { mutate: addComment } = useCreateCandidacyComment();
  const refetchCandidacyComments = useRefetchCandidacyComments();

  const form = useForm<z.infer<typeof addCandidacyCommentSchema>>({
    defaultValues: {
      content: "",
    },
    resolver: zodResolver(addCandidacyCommentSchema),
  });

  function onSubmit(data: z.infer<typeof addCandidacyCommentSchema>) {
    addComment(
      {
        candidacyId,
        createCandidacyCommentRequest: { content: data.content },
      },
      {
        onSuccess: () => {
          form.reset();
          refetchCandidacyComments({ candidacyId });
        },
      },
    );
  }

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-2"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <FormField
          control={form.control}
          name="content"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Textarea
                  className="resize-none"
                  onKeyDown={
                    misc.preventArrowLeftRightConflictWithSheetNavigation
                  }
                  placeholder="Add a comment"
                  {...field}
                  autoExpand
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex justify-end">
          <Button className="rounded-full" type="submit" variant="outline">
            Add
          </Button>
        </div>
      </form>
    </Form>
  );
};

const updateCandidacyCommentSchema = z.object({
  content: z.string().min(1, {
    message: "Required",
  }),
});
const UpdateCandidacyCommentForm = ({
  candidacyId,
  candidacyCommentId,
  content,
  onClose,
}: {
  candidacyId: string;
  candidacyCommentId: number;
  content: string;
  onClose: () => void;
}) => {
  const { mutate: updateCandidacyComment } = useUpdateCandidacyComment();
  const refetchCandidacyComments = useRefetchCandidacyComments();

  const form = useForm<z.infer<typeof updateCandidacyCommentSchema>>({
    defaultValues: {
      content: content,
    },
    resolver: zodResolver(updateCandidacyCommentSchema),
  });

  function onSubmit(data: z.infer<typeof updateCandidacyCommentSchema>) {
    updateCandidacyComment(
      {
        candidacyId,
        candidacyCommentId,
        updateCandidacyCommentRequest: { content: data.content },
      },
      {
        onSuccess: () => {
          refetchCandidacyComments({ candidacyId });
          onClose();
        },
      },
    );
  }

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-2"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <FormField
          control={form.control}
          name="content"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Textarea
                  className="resize-none"
                  onKeyDown={
                    misc.preventArrowLeftRightConflictWithSheetNavigation
                  }
                  placeholder="Add a comment"
                  {...field}
                  autoExpand
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex items-center justify-end gap-2">
          <Button
            className="rounded-full"
            onClick={onClose}
            type="button"
            variant="ghost"
          >
            Cancel
          </Button>
          <Button className="rounded-full" type="submit" variant="outline">
            Update
          </Button>
        </div>
      </form>
    </Form>
  );
};

const CandidacyComment = ({
  candidacyComment,
}: {
  candidacyComment: CandidacyCommentType;
}) => {
  const { data: user, status } = useGetUser({
    userId: candidacyComment.userId,
  });
  const { mutate: deleteComment } = useDeleteCandidacyComment();
  const refetchCandidacyComments = useRefetchCandidacyComments();
  const [isEditing, setIsEditing] = useState(false);
  // TODO: This is a common pattern so we should create a reusable piece of logic.
  const currentUser = storage.getUser();
  if (!currentUser) {
    throw new Error("User not found in local storage");
  }

  if (status === "pending") {
    // TODO: Loading state.
    return null;
  } else if (status === "error") {
    // TODO: Error state.
    return null;
  }

  return (
    <>
      {isEditing ? (
        <UpdateCandidacyCommentForm
          candidacyId={candidacyComment.candidacyId}
          candidacyCommentId={candidacyComment.id}
          content={candidacyComment.content}
          onClose={() => {
            setIsEditing(false);
          }}
        />
      ) : (
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <div className="relative">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-200">
                    <p className="text-[10px] font-medium text-slate-900">
                      {user.firstName[0]}
                      {user.lastName[0]}
                    </p>
                  </div>
                  {user.role === Role.STAFFER && (
                    <JobPipelineCandidateSheetVerificationBadge className="absolute right-[-6px] top-[15px]" />
                  )}
                </div>
                <p className="text-sm font-medium text-slate-700">
                  {user.firstName} {user.lastName} •{" "}
                  {dates.timeAgoForPipeline(candidacyComment.createdAt)}
                </p>
              </div>
            </div>
            {/* Only show the edit and delete dropdown if the current user is the author of the comment. */}
            {currentUser.id === user.id && (
              <DropdownMenu>
                <DropdownMenuTrigger>
                  <Button size="icon" variant="ghost">
                    <EllipsisIcon className="h-4 w-4 text-slate-700" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[110px] p-0">
                  <DropdownMenuItem
                    onClick={() => {
                      setIsEditing(true);
                    }}
                  >
                    <EditIcon className="h-4 w-4" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-red-500"
                    onClick={() => {
                      deleteComment(
                        {
                          candidacyId: candidacyComment.candidacyId,
                          candidacyCommentId: candidacyComment.id,
                        },
                        {
                          onSuccess: () => {
                            refetchCandidacyComments({
                              candidacyId: candidacyComment.candidacyId,
                            });
                          },
                        },
                      );
                    }}
                  >
                    <TrashIcon className="h-4 w-4" />
                    Remove
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          <p className="whitespace-pre-line text-sm text-slate-700">
            {candidacyComment.content}
          </p>
        </div>
      )}
    </>
  );
};

export const JobPipelineCandidateSheetCandidacyComments = ({
  candidacyId,
  className,
}: {
  candidacyId: string;
  className?: string;
}) => {
  const { data: comments, status } = useGetCandidacyComments({ candidacyId });

  if (status === "pending") {
    // TODO: Loading state.
    return null;
  } else if (status === "error") {
    // TODO: Error state.
    return null;
  }

  return (
    <div className={clsx("flex flex-col gap-4", className)}>
      <AddCandidacyCommentForm candidacyId={candidacyId} />
      <div className="flex flex-col gap-4">
        {comments.map((comment) => (
          <CandidacyComment key={comment.id} candidacyComment={comment} />
        ))}
      </div>
    </div>
  );
};
